import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Star } from 'lucide-react';
import { post } from '../../utils/api';
import { Button } from '../shadcn/button';
import Logo from '../../media/Transparent-Star-Only-Logo.png';

const GOOGLE_REVIEW_URL = process.env.REACT_APP_GOOGLE_REVIEW_URL;

const ReviewLandingPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const ratingParam = parseInt(searchParams.get('rating') ?? '', 10);

  // 'loading' | 'redirectToGoogle' | 'showFeedbackForm' | 'submitted' | 'error'
  const [state, setState] = useState('loading');
  const [feedback, setFeedback] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [clickToken, setClickToken] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const submit = async () => {
      if (!token || Number.isNaN(ratingParam) || ratingParam < 1 || ratingParam > 5) {
        if (!cancelled) setState('error');
        return;
      }

      try {
        const response = await post('/review/email-rate', {
          token,
          rating: ratingParam,
        });
        if (cancelled) return;
        if (response?.clickToken) setClickToken(response.clickToken);
        setState(response?.redirectToGoogle ? 'redirectToGoogle' : 'showFeedbackForm');
      } catch (err) {
        if (!cancelled) setState('error');
      }
    };

    submit();
    return () => {
      cancelled = true;
    };
  }, [token, ratingParam]);

  const handleGoogleClick = () => {
    // Fire-and-forget — the user is about to navigate away in a new tab.
    if (!clickToken) return;
    post('/review/email-google-clicked', { token: clickToken }).catch(() => {
      // Non-fatal — analytics signal only.
    });
  };

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    if (submittingFeedback) return;
    setSubmittingFeedback(true);
    try {
      await post('/review/email-feedback', {
        token,
        feedback: feedback.trim(),
      });
      setState('submitted');
    } catch (err) {
      // Token may already have been invalidated — show generic confirmation rather than block
      setState('submitted');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-6rem)] flex items-center justify-center px-4 py-10 bg-[#F7FAEE]">
      <div className="max-w-md w-full bg-white rounded-xl shadow-md p-8">
        <div className="flex justify-center mb-6">
          <img src={Logo} alt="Nova Learning" className="h-16 w-16" />
        </div>

        {state === 'loading' && (
          <div className="text-center text-gray-700">Loading…</div>
        )}

        {state === 'redirectToGoogle' && (
          <div className="text-center">
            <RatingStars value={ratingParam} />
            <h1 className="text-2xl font-semibold text-[#2F1E6B] mt-4 mb-2">
              Thank you!
            </h1>
            <p className="text-gray-700 mb-6">
              We're so glad your child is enjoying Nova Learning. Would you share your experience on Google?
            </p>
            {GOOGLE_REVIEW_URL ? (
              <a
                href={GOOGLE_REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleGoogleClick}
              >
                <Button className="bg-[#5C3BD1] hover:bg-[#2F1E6B] text-white w-full">
                  Leave a Google Review
                </Button>
              </a>
            ) : (
              <p className="text-sm text-gray-500">
                Google review link not configured.
              </p>
            )}
            <Link
              to="/"
              className="block mt-4 text-sm text-gray-500 hover:text-[#2F1E6B] underline"
            >
              No thanks
            </Link>
          </div>
        )}

        {state === 'showFeedbackForm' && (
          <div>
            <RatingStars value={ratingParam} />
            <h1 className="text-2xl font-semibold text-[#2F1E6B] mt-4 mb-2 text-center">
              We're sorry to hear that.
            </h1>
            <p className="text-gray-700 mb-4 text-center">
              Help us understand what went wrong so we can do better.
            </p>
            <form onSubmit={handleSubmitFeedback}>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                maxLength={2000}
                rows={5}
                placeholder="Tell us what went wrong (optional)"
                className="w-full border border-gray-300 rounded-md p-3 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#5C3BD1]"
              />
              <Button
                type="submit"
                disabled={submittingFeedback}
                className="w-full mt-4 bg-[#5C3BD1] hover:bg-[#2F1E6B] text-white"
              >
                {submittingFeedback ? 'Submitting…' : 'Submit feedback'}
              </Button>
            </form>
          </div>
        )}

        {state === 'submitted' && (
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-[#2F1E6B] mb-2">
              Thank you for letting us know.
            </h1>
            <p className="text-gray-700">
              We'll look into this. Your feedback helps us improve.
            </p>
            <Link
              to="/"
              className="block mt-6 text-sm text-[#5C3BD1] hover:text-[#2F1E6B] underline"
            >
              Return to homepage
            </Link>
          </div>
        )}

        {state === 'error' && (
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-[#2F1E6B] mb-2">
              This link is no longer valid.
            </h1>
            <p className="text-gray-700">
              It may have already been used or expired.
            </p>
            <Link
              to="/"
              className="block mt-6 text-sm text-[#5C3BD1] hover:text-[#2F1E6B] underline"
            >
              Return to homepage
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

const RatingStars = ({ value }) => (
  <div className="flex justify-center gap-1">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        className={
          n <= value
            ? 'h-7 w-7 fill-[#F4B400] text-[#F4B400]'
            : 'h-7 w-7 text-gray-300'
        }
      />
    ))}
  </div>
);

export default ReviewLandingPage;
